import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n, { RTL_LANGS } from "@/i18n";
import { INLINE_I18N } from "@/data/inlineI18n";

const LangContext = createContext(null);

const applyDir = (l) => {
  document.documentElement.setAttribute("dir", RTL_LANGS.includes(l) ? "rtl" : "ltr");
  document.documentElement.setAttribute("lang", l);
};

export const LangProvider = ({ children }) => {
  const [lang, setLangState] = useState(i18n.language || "id");
  const { i18n: inst } = useTranslation();

  useEffect(() => { applyDir(lang); }, [lang]);

  const setLang = useCallback((l) => {
    setLangState(l);
    inst.changeLanguage(l);
    localStorage.setItem("lang", l);
  }, [inst]);

  // Pick a translated field from {id,en,ar,...} with fallback. Inline {id,en,ar} objects get the other
  // 8 languages from INLINE_I18N (keyed by the Indonesian id text); the object's own values always win.
  // Stable per language so effects/memos may list `tc` as a dependency.
  const tc = useCallback((obj) => {
    if (!obj) return "";
    if (typeof obj === "string") return obj;
    const extra = obj.id && INLINE_I18N[obj.id];
    const merged = extra ? { ...extra, ...obj } : obj;
    return merged[lang] || merged.en || merged.id || Object.values(merged)[0] || "";
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, tc, isRtl: RTL_LANGS.includes(lang) }), [lang, setLang, tc]);
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);
